import { useNavigate } from '@tanstack/react-router';
import type { Row } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import type React from 'react';
import { useState } from 'react';
import { type DragSourceMonitor, useDrag } from 'react-dnd';
import { GALLERY_SIZE, HOME_PATH } from 'src/constants';
import { useInitialNavParams } from 'src/helpers/filters';
import { MediaCardPreviewModal } from '../AkomiMediaCard/MediaCardPreviewModal';
import { getCellClassName, getListViewColumnStyle, getListViewFillColumnId } from './ListViewColumnSizing';
import { ListViewRowContext } from './ListViewRowContext';
import type {
  CollectionListViewRow,
  DensityConfig,
  HighlightTerms,
  ListViewRow,
  ListViewRowContextValue,
} from './listView.types';

const EMPTY_FIELD_MAP = new Map();

interface ListViewCollectionRowProps {
  row: Row<ListViewRow>;
  collection: CollectionListViewRow;
  index: number;
  classes: Record<string, string>;
  density: DensityConfig;
  highlightTerms: HighlightTerms;
}

const ListViewCollectionRow: React.FC<ListViewCollectionRowProps> = ({
  row,
  collection: collectionRow,
  index,
  classes,
  density,
  highlightTerms,
}) => {
  const { collection } = collectionRow;

  const [showPreview, setShowPreview] = useState(false);
  const navigate = useNavigate();
  const buildNavParams = useInitialNavParams(GALLERY_SIZE);

  const handleVideoClick = (id: string): void => {
    const navParams = buildNavParams(`${HOME_PATH}/gallery/${id}`);
    navigate({ to: navParams.to, search: navParams.search });
  };

  // Drag & Drop
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'CARD_AKOMI',
      item: { id: collection.id },
      collect: (monitor: DragSourceMonitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    [collection.id],
  );

  // Compute cell styles
  const visibleCells = row.getVisibleCells();
  const visibleColumnIds = visibleCells.map((cell) => cell.column.id);
  const fillColumnId = getListViewFillColumnId(visibleColumnIds);
  const cellStyleById = Object.fromEntries(
    visibleCells.map((cell) => [
      cell.column.id,
      getListViewColumnStyle(cell.column.id, cell.column.getSize(), fillColumnId),
    ]),
  ) as Record<string, React.CSSProperties>;

  const isOdd = index % 2 === 1;

  const rowClassNames = [
    classes.bodyRow,
    isOdd ? classes.bodyRowOdd : '',
    classes.bodyRowGallery,
    isDragging ? classes.bodyRowDragging : '',
  ]
    .filter(Boolean)
    .join(' ');

  const contextValue: ListViewRowContextValue = {
    rowType: 'collection',
    collection,
    fieldsBySlug: EMPTY_FIELD_MAP,
    loading: false,
    highlightTerms,
    density,
    classes,
    handleNavigate: handleVideoClick,
    onShowPreview: () => setShowPreview(true),
  };

  return (
    <ListViewRowContext.Provider value={contextValue}>
      <tr
        ref={drag as unknown as React.Ref<HTMLTableRowElement>}
        className={rowClassNames}
        style={{ cursor: 'pointer' }}
      >
        {visibleCells.map((cell) => (
          <td
            key={cell.id}
            className={getCellClassName(cell.column.id, classes, cell.column.columnDef.meta)}
            style={cellStyleById[cell.column.id]}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        ))}
      </tr>
      {showPreview && (
        <MediaCardPreviewModal itemID={collection.id} pagination={true} onClose={() => setShowPreview(false)} />
      )}
    </ListViewRowContext.Provider>
  );
};

export default ListViewCollectionRow;
